/**
 * Iter 64 — Storage degraded banner.
 *
 * Renders a thin amber warning bar at the top of the app shell while the
 * Emergent Object Storage gateway is in a degraded state. Toggled via the
 * workspace flag `storage_degraded_banner` (boolean). Dismissable per tab
 * session — clicking X stores a key in `sessionStorage` so the banner stays
 * hidden until the next page load or new tab.
 */
import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';

const SESSION_KEY = 'stellar_storage_banner_dismissed';

export function StorageDegradedBanner({ visible }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (!visible || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-amber-500/15 border-b border-amber-500/30 text-amber-100"
      data-testid="storage-degraded-banner"
    >
      <div className="max-w-screen-2xl mx-auto px-3 py-1.5 flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-300 shrink-0" />
        <p className="text-[11.5px] leading-snug flex-1 min-w-0">
          <span className="font-semibold">Storage provider recovering from earlier outage.</span>{' '}
          File uploads may take longer than usual or fail intermittently. If an
          upload fails, please retry — your already-uploaded chunks are kept on
          the server for 24 hours.
        </p>
        <button
          onClick={() => {
            try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
            setDismissed(true);
          }}
          className="text-amber-200/70 hover:text-amber-100 shrink-0 p-0.5"
          data-testid="storage-degraded-banner-dismiss"
          aria-label="Dismiss for this session"
          title="Dismiss for this session"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
